/**
 * Card-id → dual-encoding glyph + i18n label KEY (spec §3.2–§3.6,
 * WCAG 1.4.1). Card text is derived from the structured id (the rule
 * schema), never from images — the label is a locale key resolved by the
 * caller via `useLocale().t(...)`, so both ko-KR and en-US are covered.
 *
 * `icon` is the shared-sprite shape encoder; `glyph` is the text fallback.
 * Per the wireframe, equipment keeps its hazard colour while power / turbo /
 * spyglass sit on a neutral surface so the multi-colour glyphs read.
 *
 * The client only ever renders ITS OWN hand (other hands are `null` in the
 * ViewerState). Card ids: `eq-<colour>-NN`, `turbo-NN`, `pw-<kind>-NN`,
 * treasure ids. We parse the structured prefix — never substring-match.
 */

import type { CelestiaIconId } from "./CelestiaIconSprite";

export interface CardLabel {
  readonly glyph: string;
  /** Shared-sprite icon id; undefined → render the text `glyph`. */
  readonly icon?: CelestiaIconId;
  /** i18n key — resolve with `t(labelKey)` at the call site. */
  readonly labelKey: string;
  readonly colorVar: string;
  /** Glyph / text ink for contrast on the colour surface. */
  readonly ink: string;
}

const INK_DARK = "var(--cn-celestia-card-label)";
const INK_LIGHT = "#ffffff";
const NEUTRAL = "--cn-celestia-bottom-sheet-bg";

const EQUIP: Readonly<
  Record<
    string,
    {
      glyph: string;
      icon: CelestiaIconId;
      labelKey: string;
      colorVar: string;
      ink: string;
    }
  >
> = {
  blue: {
    glyph: "C",
    icon: "ic-fog",
    labelKey: "card_eq_compass",
    colorVar: "--cn-celestia-equip-compass",
    ink: INK_LIGHT,
  },
  yellow: {
    glyph: "L",
    icon: "ic-lightning",
    labelKey: "card_eq_arrester",
    colorVar: "--cn-celestia-equip-arrester",
    ink: INK_DARK,
  },
  red: {
    glyph: "F",
    icon: "ic-birds",
    labelKey: "card_eq_foghorn",
    colorVar: "--cn-celestia-equip-foghorn",
    ink: INK_LIGHT,
  },
  black: {
    glyph: "P",
    icon: "ic-pirate",
    labelKey: "card_eq_cannon",
    colorVar: "--cn-celestia-equip-cannon",
    ink: INK_LIGHT,
  },
};

const POWER_GLYPH: Readonly<Record<string, string>> = {
  ejection: "EJ",
  jetpack: "JP",
  alternativeRoute: "AR",
  windGust: "WG",
};

const POWER_ICON: Readonly<Record<string, CelestiaIconId>> = {
  ejection: "ic-grapple",
  jetpack: "ic-jetpack",
  alternativeRoute: "ic-altroute",
  windGust: "ic-windgust",
};

export function cardLabel(id: string): CardLabel {
  const parts = id.split("-");
  if (parts[0] === "eq" && parts[1] && EQUIP[parts[1]]) {
    const e = EQUIP[parts[1]];
    return {
      glyph: e.glyph,
      icon: e.icon,
      labelKey: e.labelKey,
      colorVar: e.colorVar,
      ink: e.ink,
    };
  }
  if (parts[0] === "turbo") {
    return {
      glyph: "T",
      icon: "ic-turbo",
      labelKey: "card_turbo",
      colorVar: NEUTRAL,
      ink: INK_DARK,
    };
  }
  if (parts[0] === "pw" && parts[1]) {
    return {
      glyph: POWER_GLYPH[parts[1]] ?? "PW",
      icon: POWER_ICON[parts[1]],
      labelKey: `card_pw_${parts[1]}`,
      colorVar: NEUTRAL,
      ink: INK_DARK,
    };
  }
  if (id.includes("spyglass")) {
    return {
      glyph: "SG",
      icon: "ic-spyglass",
      labelKey: "card_spyglass",
      colorVar: NEUTRAL,
      ink: INK_DARK,
    };
  }
  return {
    glyph: "?",
    labelKey: "card_unknown",
    colorVar: "--cn-celestia-card-outline",
    ink: INK_DARK,
  };
}
